import { useRouter } from "next/router";
import { UseFormReturn, useWatch } from "react-hook-form";

import { useTranslation } from "../../core/translation.core";
import { BannerFormData } from "../../types/banner";
import { LocaleTextAresDefaultLocale } from "../locale/locale.comp";
import MediaSelector, { MediaSelectorMode } from "../media/mediaSelector.comp";
import { EditorSection, SubmitPanel, Title } from "../reuseables/editor.comp";
import { LabelAndInput, LabelAndSelect } from "../reuseables/form.comp";

export default function BannerEditor({
  methods,
  onSubmit,
  btnName,
}: {
  methods: UseFormReturn<BannerFormData, any>;
  onSubmit: (data: BannerFormData) => Promise<void>;
  btnName: string;
}) {
  const { t, msg } = useTranslation();

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)}>
      <EditorSection>
        <Title>{t(msg.product.generalInfo)}</Title>
        <div>
          <LabelAndSelect
            methods={methods}
            fieldName="groupName"
            label={t(msg.banner.selectBannerType)}
            options={[
              { label: t(msg.banner.topBarBanners), value: "topBar" },
              { label: t(msg.banner.headerBanners), value: "header" },
              { label: t(msg.banner.footerBanners), value: "footer" },
              { label: t(msg.banner.sidebarBanners), value: "sidebar" },
              { label: t(msg.banner.carouselBanners), value: "carousel" },
              { label: t(msg.banner.sectionBanners), value: "section" },
            ]}
            setDefault={false}
            topLabelCss="block"
          />

          <MediaSelector
            id="product-gallery"
            mode={MediaSelectorMode.single}
            label={t(msg.product.productGallery)}
            defaultSelectedIds={[methods.getValues("mediaId")]}
            className="mh-10 my-2"
            onChanged={(ids) => {
              methods.setValue("mediaId", ids[0]);
            }}
          />

          <LocaleTextAresDefaultLocale register={methods.register} label={t(msg.banner.html)} fieldName="html" />
          <LabelAndInput register={methods.register} label={t(msg.general.url)} fieldName="url" />
        </div>
        <SubmitButton methods={methods} btnName={btnName} />
      </EditorSection>
    </form>
  );
}

const SubmitButton = ({ methods, btnName }: { methods: UseFormReturn<BannerFormData, any>; btnName: string }) => {
  const { t, msg } = useTranslation();
  const { push } = useRouter();
  const data = useWatch({ control: methods.control });

  const isValid = (data.mediaId || data.html) && data.groupName;

  return (
    <SubmitPanel>
      <button onClick={() => push("/banner")}>{t(msg.form.cancel)}</button>
      <input disabled={!isValid} type="submit" value={btnName} className="w-fit px-3 py-2" />
    </SubmitPanel>
  );
};
